import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import apiClient from '@/lib/axios';

export interface Category {
  id: number;
  category_code: string;
  category_name: string;
  unit: string;
  sort_order: number;
  is_active: number;
  sale_price?: number;
  recycle_price?: number;
}

interface CategoryContextType {
  categories: Category[];
  activeCategories: Category[];
  categoryMap: Map<string, Category>;
  categoryCodes: string[];
  loading: boolean;
  error: string | null;
  refreshCategories: () => Promise<void>;
  getCategoryByCode: (code: string) => Category | undefined;
  getCategoryName: (code: string, fallback?: string) => string;
  isValidCategory: (code: string) => boolean;
}

const CategoryContext = createContext<CategoryContextType>({
  categories: [],
  activeCategories: [],
  categoryMap: new Map(),
  categoryCodes: [],
  loading: true,
  error: null,
  refreshCategories: async () => {},
  getCategoryByCode: () => undefined,
  getCategoryName: (code, fallback) => fallback || code,
  isValidCategory: () => false,
});

export const useCategories = () => useContext(CategoryContext);

interface CategoryProviderProps {
  children: ReactNode;
}

// 本地缓存键
const CATEGORY_CACHE_KEY = 'app_categories';
const CATEGORY_CACHE_TIME_KEY = 'app_categories_time';
const CACHE_DURATION = 5 * 60 * 1000; // 5分钟

export const CategoryProvider: React.FC<CategoryProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 计算属性
  const activeCategories = categories.filter(c => c.is_active === 1);
  const categoryMap = new Map(categories.map(c => [c.category_code, c]));
  const categoryCodes = activeCategories.map(c => c.category_code);

  const fetchCategories = useCallback(async (force = false) => {
    // 检查缓存
    if (!force) {
      const cachedTime = localStorage.getItem(CATEGORY_CACHE_TIME_KEY);
      const cached = localStorage.getItem(CATEGORY_CACHE_KEY);
      if (cached && cachedTime && Date.now() - parseInt(cachedTime) < CACHE_DURATION) {
        try {
          const parsed = JSON.parse(cached);
          setCategories(parsed);
          setLoading(false);
          return;
        } catch {
          // 缓存解析失败，继续获取
        }
      }
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get('/settings/categories/all');
      if (response.data.success) {
        const data = response.data.data || [];
        setCategories(data);
        
        // 更新缓存
        localStorage.setItem(CATEGORY_CACHE_KEY, JSON.stringify(data));
        localStorage.setItem(CATEGORY_CACHE_TIME_KEY, Date.now().toString());
      } else {
        throw new Error(response.data.message || '获取品类失败');
      }
    } catch (err: any) {
      console.error('获取品类配置失败:', err);
      setError(err.message || '获取品类配置失败');
      
      // 尝试使用缓存数据（即使已过期）
      const cached = localStorage.getItem(CATEGORY_CACHE_KEY);
      if (cached) {
        try {
          setCategories(JSON.parse(cached));
        } catch {
          // 使用默认数据
          setCategories(getDefaultCategories());
        }
      } else {
        setCategories(getDefaultCategories());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getCategoryByCode = useCallback((code: string) => {
    return categoryMap.get(code);
  }, [categoryMap]);

  const getCategoryName = useCallback((code: string, fallback?: string) => {
    const cat = categoryMap.get(code);
    return cat?.category_name || fallback || code;
  }, [categoryMap]);

  const isValidCategory = useCallback((code: string) => {
    const cat = categoryMap.get(code);
    return !!cat && cat.is_active === 1;
  }, [categoryMap]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 监听品类更新事件
  useEffect(() => {
    const handleCategoriesUpdated = () => {
      fetchCategories(true);
    };
    
    window.addEventListener('categories_updated', handleCategoriesUpdated);
    return () => window.removeEventListener('categories_updated', handleCategoriesUpdated);
  }, [fetchCategories]);

  return (
    <CategoryContext.Provider
      value={{
        categories,
        activeCategories,
        categoryMap,
        categoryCodes,
        loading,
        error,
        refreshCategories: () => fetchCategories(true),
        getCategoryByCode,
        getCategoryName,
        isValidCategory,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

// 默认品类配置（当API不可用时使用）
function getDefaultCategories(): Category[] {
  return [
    { id: 1, category_code: 'paper', category_name: '纸皮', unit: 'kg', sort_order: 1, is_active: 1, sale_price: 1.2, recycle_price: 0.9 },
    { id: 2, category_code: 'black_blue', category_name: '黑筐', unit: 'kg', sort_order: 2, is_active: 1, sale_price: 0.8, recycle_price: 0.5 },
    { id: 3, category_code: 'white_red', category_name: '红筐', unit: 'kg', sort_order: 3, is_active: 1, sale_price: 0.9, recycle_price: 0.5 },
    { id: 4, category_code: 'banana_box', category_name: '香蕉箱', unit: '个', sort_order: 4, is_active: 1, sale_price: 0.5, recycle_price: 0.5 },
    { id: 5, category_code: 'plastic', category_name: '塑料', unit: 'kg', sort_order: 5, is_active: 1, sale_price: 0.8, recycle_price: 0.5 },
    { id: 6, category_code: 'langk', category_name: '蓝筐', unit: 'kg', sort_order: 6, is_active: 1, sale_price: 0.7, recycle_price: 0.0 },
    { id: 7, category_code: 'bmt', category_name: '透明白', unit: 'kg', sort_order: 7, is_active: 1, sale_price: 0.0, recycle_price: 0.0 },
  ];
}

// 品类名称到编码的映射（用于导入）
export const CATEGORY_NAME_MAP: Record<string, string> = {
  '纸皮': 'paper',
  '废纸': 'paper',
  '黑筐': 'black_blue',
  '黑蓝筐': 'black_blue',
  '蓝筐': 'langk',
  '白筐': 'white_red',
  '红筐': 'white_red',
  '白红筐': 'white_red',
  '香蕉箱': 'banana_box',
  '塑料': 'plastic',
  '透明白': 'bmt',
  '其他': 'other',
  '其它': 'other',
};

// 根据名称获取品类编码
export function getCategoryCodeByName(name: string): string {
  return CATEGORY_NAME_MAP[name] || 'other';
}

export default CategoryContext;
